import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Download,
  BarChart3,
  Users,
  Clock,
  ArrowLeft,
  Filter,
  TrendingUp,
  TrendingDown,
  Trophy,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ChevronRight,
} from 'lucide-react';
import { formatLocalDate, formatLocalTime } from '../utils/dateUtils';
import { testResultsApi, TestResult, TestStatistics } from '@/lib/testResultsApi';
import { getErrorInfo } from '@/lib/errorUtils';

// ─── helpers ────────────────────────────────────────────────────────────────

const PASS_THRESHOLD = 60; // %

function scoreColor(pct: number) {
  if (pct >= 75) return 'text-green-600';
  if (pct >= 60) return 'text-yellow-600';
  return 'text-red-600';
}

function scoreBg(pct: number) {
  if (pct >= 75) return 'bg-green-500';
  if (pct >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
}

function completionBg(pct: number) {
  if (pct >= 75) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ─── sub-components (pure, no state) ────────────────────────────────────────

/** Horizontal bar used in histograms and breakdowns */
function Bar({ value, max, color, label, count }: { value: number; max: number; color: string; label: string; count: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-20 text-right text-muted-foreground shrink-0">{label}</span>
      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-muted-foreground shrink-0">{count}</span>
    </div>
  );
}

/** Simple SVG donut slice — no library needed */
function DonutChart({ slices }: { slices: { label: string; value: number; color: string }[] }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) return <div className="text-center text-muted-foreground text-sm py-4">No data</div>;

  let cumulativeAngle = -Math.PI / 2;
  const cx = 60, cy = 60, r = 48, inner = 28;

  const paths = slices
    .filter(s => s.value > 0)
    .map(s => {
      const angle = (s.value / total) * 2 * Math.PI;
      const x1 = cx + r * Math.cos(cumulativeAngle);
      const y1 = cy + r * Math.sin(cumulativeAngle);
      cumulativeAngle += angle;
      const x2 = cx + r * Math.cos(cumulativeAngle);
      const y2 = cy + r * Math.sin(cumulativeAngle);
      const xi1 = cx + inner * Math.cos(cumulativeAngle);
      const yi1 = cy + inner * Math.sin(cumulativeAngle);
      const xi2 = cx + inner * Math.cos(cumulativeAngle - angle);
      const yi2 = cy + inner * Math.sin(cumulativeAngle - angle);
      const largeArc = angle > Math.PI ? 1 : 0;
      return { path: `M${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} L${xi1},${yi1} A${inner},${inner} 0 ${largeArc},0 ${xi2},${yi2} Z`, color: s.color, label: s.label, value: s.value };
    });

  return (
    <div className="flex items-center gap-4">
      <svg width="120" height="120" viewBox="0 0 120 120">
        {paths.map((p, i) => <path key={i} d={p.path} fill={p.color} />)}
        <text x="60" y="56" textAnchor="middle" fontSize="11" fill="#6b7280">Total</text>
        <text x="60" y="70" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#111827">{total}</text>
      </svg>
      <div className="space-y-1.5">
        {slices.filter(s => s.value > 0).map(s => (
          <div key={s.label} className="flex items-center gap-2 text-sm">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-semibold ml-1">{s.value}</span>
            <span className="text-xs text-muted-foreground">({((s.value / total) * 100).toFixed(0)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Statistics tab ──────────────────────────────────────────────────────────

interface StatisticsTabProps {
  allResults: TestResult[];
}

function StatisticsTab({ allResults }: StatisticsTabProps) {
  const [stats, setStats] = useState<TestStatistics[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<keyof TestStatistics>('averageScore');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    testResultsApi.getTestStatistics()
      .then(data => { if (!cancelled) { setStats(data); setStatsLoading(false); } })
      .catch(err => { if (!cancelled) { setStatsError(getErrorInfo(err).message); setStatsLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const sorted = useMemo(() => {
    return [...stats].sort((a, b) => {
      const av = a[sortCol] as number, bv = b[sortCol] as number;
      return sortAsc ? av - bv : bv - av;
    });
  }, [stats, sortCol, sortAsc]);

  const selected = useMemo(
    () => selectedTestId ? stats.find(s => s.testId === selectedTestId) ?? null : null,
    [stats, selectedTestId]
  );

  // Per-test individual results (from allResults prop, filtered by testId)
  const testResults = useMemo(
    () => selected ? allResults.filter(r => r.testId === selected.testId) : [],
    [allResults, selected]
  );

  const completedResults = useMemo(
    () => testResults.filter(r => r.status === 'completed'),
    [testResults]
  );

  // Score distribution buckets
  const buckets = useMemo(() => {
    const b = [
      { label: '0–20%', count: 0, color: 'bg-red-500' },
      { label: '21–40%', count: 0, color: 'bg-orange-500' },
      { label: '41–60%', count: 0, color: 'bg-yellow-500' },
      { label: '61–80%', count: 0, color: 'bg-blue-500' },
      { label: '81–100%', count: 0, color: 'bg-green-500' },
    ];
    completedResults.forEach(r => {
      const idx = Math.min(Math.floor(r.percentage / 20), 4);
      b[idx].count++;
    });
    return b;
  }, [completedResults]);

  const maxBucketCount = Math.max(...buckets.map(b => b.count), 1);

  // Class/batch breakdown
  const classBatches = useMemo(() => {
    const map = new Map<string, { students: number; totalPct: number; completions: number }>();
    testResults.forEach(r => {
      const key = r.className || 'Unassigned';
      const existing = map.get(key) ?? { students: 0, totalPct: 0, completions: 0 };
      existing.students++;
      if (r.status === 'completed') { existing.totalPct += r.percentage; existing.completions++; }
      map.set(key, existing);
    });
    return Array.from(map.entries())
      .map(([name, d]) => ({ name, students: d.students, avgScore: d.completions > 0 ? d.totalPct / d.completions : 0, completionRate: d.students > 0 ? (d.completions / d.students) * 100 : 0 }))
      .sort((a, b) => b.avgScore - a.avgScore);
  }, [testResults]);

  // Top & bottom 5 performers
  const ranked = useMemo(
    () => [...completedResults].sort((a, b) => b.percentage - a.percentage),
    [completedResults]
  );
  const top5 = ranked.slice(0, 5);
  const bottom5 = ranked.slice(-5).reverse();

  // Aggregate correct/wrong/unanswered
  const aggregateAnswers = useMemo(() => {
    const totals = completedResults.reduce((acc, r) => ({
      correct: acc.correct + (r.correctAnswers || 0),
      wrong: acc.wrong + (r.wrongAnswers || 0),
      unanswered: acc.unanswered + (r.unansweredQuestions || 0),
    }), { correct: 0, wrong: 0, unanswered: 0 });
    const total = totals.correct + totals.wrong + totals.unanswered;
    return { ...totals, total };
  }, [completedResults]);

  function toggleSort(col: keyof TestStatistics) {
    if (sortCol === col) setSortAsc(a => !a);
    else { setSortCol(col); setSortAsc(false); }
  }

  const SortHeader = ({ col, children }: { col: keyof TestStatistics; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => toggleSort(col)}>
      <div className="flex items-center gap-1">
        {children}
        {sortCol === col ? (sortAsc ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />) : null}
      </div>
    </TableHead>
  );

  if (statsLoading) return (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-primary mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">Loading statistics…</p>
      </div>
    </div>
  );

  if (statsError) return (
    <div className="bg-red-50 border border-red-200 rounded-md p-6 text-center">
      <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-2" />
      <p className="text-red-700 text-sm">{statsError}</p>
    </div>
  );

  if (stats.length === 0) return (
    <div className="text-center py-16 text-muted-foreground">
      <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-40" />
      <p>No statistics available yet. Results will appear once students complete tests.</p>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* ── Cross-test Summary Table ── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            All Tests — Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Test Name</TableHead>
                <SortHeader col="totalAssigned">Assigned</SortHeader>
                <SortHeader col="completionRate">Completion</SortHeader>
                <SortHeader col="averageScore">Avg Score</SortHeader>
                <SortHeader col="highestScore">Highest</SortHeader>
                <SortHeader col="lowestScore">Lowest</SortHeader>
                <SortHeader col="averageTime">Avg Time</SortHeader>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(s => (
                <TableRow
                  key={s.testId}
                  className={`cursor-pointer hover:bg-muted/50 ${selectedTestId === s.testId ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                  onClick={() => setSelectedTestId(prev => prev === s.testId ? null : s.testId)}
                >
                  <TableCell className="font-medium">{s.testTitle}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="font-semibold">{s.totalCompleted}</span>
                      <span className="text-muted-foreground"> / {s.totalAssigned}</span>
                    </div>
                    {s.totalInProgress > 0 && <div className="text-xs text-yellow-600">{s.totalInProgress} in progress</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${completionBg(s.completionRate)}`} style={{ width: `${Math.min(s.completionRate, 100)}%` }} />
                      </div>
                      <span className={`text-sm font-semibold ${s.completionRate < 50 ? 'text-red-600' : s.completionRate < 75 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {s.completionRate.toFixed(0)}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`font-semibold ${scoreColor(s.averageScore)}`}>{s.averageScore.toFixed(1)}%</span>
                  </TableCell>
                  <TableCell><span className="text-green-600 font-medium">{s.highestScore.toFixed(1)}%</span></TableCell>
                  <TableCell><span className="text-red-600 font-medium">{s.lowestScore.toFixed(1)}%</span></TableCell>
                  <TableCell><span className="text-muted-foreground">{s.averageTime.toFixed(0)}m</span></TableCell>
                  <TableCell>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${selectedTestId === s.testId ? 'rotate-90' : ''}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Deep-dive panel (shown when a test row is selected) ── */}
      {selected && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold">{selected.testTitle}</h2>
            <Badge variant="outline">Deep Dive</Badge>
          </div>

          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Completion Rate', value: `${selected.completionRate.toFixed(0)}%`, sub: `${selected.totalCompleted} / ${selected.totalAssigned} students`, color: completionBg(selected.completionRate), icon: <Users className="w-5 h-5" /> },
              { label: 'Average Score', value: `${selected.averageScore.toFixed(1)}%`, sub: PASS_THRESHOLD + '% pass threshold', color: scoreBg(selected.averageScore), icon: <BarChart3 className="w-5 h-5" /> },
              { label: 'Highest Score', value: `${selected.highestScore.toFixed(1)}%`, sub: 'Top performer', color: 'bg-green-500', icon: <Trophy className="w-5 h-5" /> },
              { label: 'Lowest Score', value: `${selected.lowestScore.toFixed(1)}%`, sub: 'Needs intervention', color: selected.lowestScore < PASS_THRESHOLD ? 'bg-red-500' : 'bg-yellow-500', icon: <AlertTriangle className="w-5 h-5" /> },
              { label: 'Avg Time Taken', value: `${selected.averageTime.toFixed(0)}m`, sub: 'Per completed attempt', color: 'bg-blue-500', icon: <Clock className="w-5 h-5" /> },
            ].map(t => (
              <Card key={t.label}>
                <CardContent className="p-4">
                  <div className={`inline-flex p-2 rounded-lg ${t.color} bg-opacity-10 mb-2`}>
                    <span className={t.color.replace('bg-', 'text-')}>{t.icon}</span>
                  </div>
                  <div className="text-2xl font-bold">{t.value}</div>
                  <div className="text-xs text-muted-foreground">{t.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t.sub}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">

            {/* Score Distribution */}
            <Card>
              <CardHeader><CardTitle className="text-base">Score Distribution</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {completedResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No completed attempts yet.</p>
                ) : (
                  buckets.map(b => (
                    <Bar key={b.label} label={b.label} value={b.count} max={maxBucketCount} count={b.count} color={b.color} />
                  ))
                )}
              </CardContent>
            </Card>

            {/* Attempt Funnel + Pass/Fail Donut */}
            <Card>
              <CardHeader><CardTitle className="text-base">Attempt Funnel</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Funnel bars */}
                {(() => {
                  const passed = completedResults.filter(r => r.percentage >= PASS_THRESHOLD).length;
                  const funnel = [
                    { label: 'Assigned', count: selected.totalAssigned, color: 'bg-gray-400' },
                    { label: 'Started', count: selected.totalCompleted + selected.totalInProgress, color: 'bg-blue-400' },
                    { label: 'Completed', count: selected.totalCompleted, color: 'bg-blue-600' },
                    { label: `Passed (≥${PASS_THRESHOLD}%)`, count: passed, color: 'bg-green-500' },
                  ];
                  return funnel.map(f => (
                    <Bar key={f.label} label={f.label} value={f.count} max={selected.totalAssigned} count={f.count} color={f.color} />
                  ));
                })()}

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Status Breakdown</p>
                  <DonutChart slices={[
                    { label: 'Passed', value: completedResults.filter(r => r.percentage >= PASS_THRESHOLD).length, color: '#16a34a' },
                    { label: 'Failed', value: completedResults.filter(r => r.percentage < PASS_THRESHOLD).length, color: '#dc2626' },
                    { label: 'In Progress', value: selected.totalInProgress, color: '#ca8a04' },
                    { label: 'Not Started', value: Math.max(0, selected.totalAssigned - selected.totalCompleted - selected.totalInProgress), color: '#9ca3af' },
                  ]} />
                </div>
              </CardContent>
            </Card>

          </div>

          <div className="grid md:grid-cols-2 gap-6">

            {/* Correct / Wrong / Unanswered Aggregate */}
            <Card>
              <CardHeader><CardTitle className="text-base">Answer Breakdown (Cohort)</CardTitle></CardHeader>
              <CardContent>
                {completedResults.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No completed attempts yet.</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex gap-4 text-sm mb-4">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-700">{aggregateAnswers.correct}</span>
                        <span className="text-muted-foreground">correct</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-red-700">{aggregateAnswers.wrong}</span>
                        <span className="text-muted-foreground">wrong</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MinusCircle className="w-4 h-4 text-amber-600" />
                        <span className="font-semibold text-amber-700">{aggregateAnswers.unanswered}</span>
                        <span className="text-muted-foreground">unanswered</span>
                      </div>
                    </div>
                    {aggregateAnswers.total > 0 && (
                      <div className="w-full h-5 rounded-full overflow-hidden flex">
                        <div className="bg-green-500 h-full" style={{ width: `${(aggregateAnswers.correct / aggregateAnswers.total) * 100}%` }} />
                        <div className="bg-red-500 h-full" style={{ width: `${(aggregateAnswers.wrong / aggregateAnswers.total) * 100}%` }} />
                        <div className="bg-amber-400 h-full" style={{ width: `${(aggregateAnswers.unanswered / aggregateAnswers.total) * 100}%` }} />
                      </div>
                    )}
                    {aggregateAnswers.unanswered > 0 && aggregateAnswers.total > 0 && ((aggregateAnswers.unanswered / aggregateAnswers.total) > 0.2) && (
                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        High unanswered rate ({((aggregateAnswers.unanswered / aggregateAnswers.total) * 100).toFixed(0)}%) — students may be running out of time.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Class / Batch Comparison */}
            <Card>
              <CardHeader><CardTitle className="text-base">Class / Batch Comparison</CardTitle></CardHeader>
              <CardContent>
                {classBatches.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No class data available.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Class</TableHead>
                        <TableHead className="text-right">Students</TableHead>
                        <TableHead className="text-right">Avg Score</TableHead>
                        <TableHead className="text-right">Completion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classBatches.map(c => (
                        <TableRow key={c.name}>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-right">{c.students}</TableCell>
                          <TableCell className={`text-right font-semibold ${scoreColor(c.avgScore)}`}>{c.avgScore.toFixed(1)}%</TableCell>
                          <TableCell className="text-right">
                            <span className={c.completionRate < 50 ? 'text-red-600' : c.completionRate < 75 ? 'text-yellow-600' : 'text-green-600'}>
                              {c.completionRate.toFixed(0)}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Top & Bottom Performers */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {top5.length === 0 ? <p className="text-sm text-muted-foreground">No completed attempts yet.</p> : (
                  <div className="space-y-2">
                    {top5.map((r, i) => (
                      <div key={r.id} className="flex items-center gap-3 text-sm">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-200 text-gray-600'}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{r.studentName}</div>
                          <div className="text-xs text-muted-foreground truncate">{r.className || 'No class'}</div>
                        </div>
                        <span className="font-bold text-green-600 shrink-0">{r.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Needs Intervention
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bottom5.length === 0 ? <p className="text-sm text-muted-foreground">No completed attempts yet.</p> : (
                  <div className="space-y-2">
                    {bottom5.map((r, i) => (
                      <div key={r.id} className="flex items-center gap-3 text-sm">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center bg-red-100 text-red-600 text-xs font-bold shrink-0">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{r.studentName}</div>
                          <div className="text-xs text-muted-foreground truncate">{r.className || 'No class'}</div>
                        </div>
                        <span className={`font-bold shrink-0 ${r.percentage < PASS_THRESHOLD ? 'text-red-600' : 'text-yellow-600'}`}>{r.percentage.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>
      )}

      {!selected && stats.length > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Click any row in the table above to drill into that test's detailed analytics.
        </p>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const TestResultsPage = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [testFilter, setTestFilter] = useState<string>('ALL');
  const [activeTab, setActiveTab] = useState('results');

  // ── Bug fix: stable loadData via useCallback prevents infinite render loop ──
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const resultsData = await testResultsApi.getAllResults(
        0,
        100, // load enough for client-side stats computation
        searchTerm || undefined,
        statusFilter !== 'ALL' ? statusFilter : undefined,
        testFilter !== 'ALL' ? testFilter : undefined
      );

      setResults(resultsData.content);
    } catch (err) {
      console.error('Failed to load test results:', err);
      const errorInfo = getErrorInfo(err);
      setError(errorInfo.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, testFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredResults = results;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'in-progress':
        return <Badge className="bg-yellow-100 text-yellow-800">In Progress</Badge>;
      case 'not-started':
        return <Badge className="bg-gray-100 text-gray-800">Not Started</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getScoreBadge = (percentage: number) => {
    if (percentage >= 90) {
      return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    } else if (percentage >= 75) {
      return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
    } else if (percentage >= 60) {
      return <Badge className="bg-yellow-100 text-yellow-800">Average</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800">Needs Improvement</Badge>;
    }
  };

  const exportResults = async () => {
    try {
      const blob = await testResultsApi.exportResults(
        searchTerm || undefined,
        statusFilter !== 'ALL' ? statusFilter : undefined,
        testFilter !== 'ALL' ? testFilter : undefined
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `test-results-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export results:', err);
      const errorInfo = getErrorInfo(err);
      setError(`Export failed: ${errorInfo.message}`);
    }
  };

  const uniqueTests = Array.from(new Set(results.map(r => r.testId)))
    .map(testId => results.find(r => r.testId === testId))
    .filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/tests')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Test Results & Analytics</h1>
        </div>
        <Button onClick={exportResults}>
          <Download className="mr-2 h-4 w-4" />
          Export Results
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">
            {error}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by test, student name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="not-started">Not Started</SelectItem>
              </SelectContent>
            </Select>
            <Select value={testFilter} onValueChange={setTestFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Test" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Tests</SelectItem>
                {uniqueTests.map((test) => (
                  <SelectItem key={test!.testId} value={test!.testId}>
                    {test!.testTitle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="results" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Individual Results
          </TabsTrigger>
          <TabsTrigger value="statistics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Test Statistics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="results" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Student Results ({filteredResults.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-blue-600 mx-auto mb-3"></div>
                    <p className="text-muted-foreground text-sm">Loading results...</p>
                  </div>
                </div>
              ) : filteredResults.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">No results found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {searchTerm || statusFilter !== 'ALL' || testFilter !== 'ALL'
                      ? 'Try adjusting your filters'
                      : 'No test results available yet'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Test</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Marks</TableHead>
                      <TableHead>Percentage</TableHead>
                      <TableHead>Questions</TableHead>
                      <TableHead>Performance</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredResults.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{result.studentName}</div>
                            <div className="text-sm text-muted-foreground">{result.studentEmail}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{result.testTitle}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{result.className || 'Unassigned'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {result.score}/{result.maxScore}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {result.percentage.toFixed(2)}%
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs space-y-0.5">
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">Total:</span>
                              <span className="font-medium">{result.totalQuestions || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-green-600">✓ {result.correctAnswers || 0}</span>
                              <span className="text-red-600">✗ {result.wrongAnswers || 0}</span>
                              <span className="text-amber-600">− {result.unansweredQuestions || 0}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getScoreBadge(result.percentage)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            {result.timeSpent}m
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(result.status)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatLocalDate(result.submittedAt)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {formatLocalTime(result.submittedAt)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics" className="space-y-4">
          <StatisticsTab allResults={results} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TestResultsPage;
